import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import SEOHead from '../../components/SEO/SEOHead';

const TripCostingPageArabic = () => {
  const { i18n } = useTranslation();

  // Set language to Arabic when component mounts
  useEffect(() => {
    if (i18n.language !== 'ar') {
      i18n.changeLanguage('ar');
    }
  }, [i18n]);

  return (
    <>
      <SEOHead 
        title="تكلفة الرحلات والربحية - إدارة الأسطول المملكة العربية السعودية"
        description="احسب تكاليف الرحلات بدقة، تتبع مصاريف الوقود، وزيادة الربحية لكل شحنة في المملكة العربية السعودية. متوافق مع ضريبة القيمة المضافة مع توزيع التكاليف التلقائي."
        keywords="تكلفة الرحلات السعودية، ربحية الأسطول، تتبع تكاليف الوقود، تسويات السائقين، تكاليف الصيانة، إدارة ضريبة القيمة المضافة المملكة، احتساب تكلفة الشحنات"
        canonicalUrl="/ar/trip-costing"
        lang="ar"
        structuredData={{
          "@context": "https://schema.org",
          "@type": "Service",
          "name": "تكلفة الرحلات وتحليل الربحية",
          "description": "حل احتساب تكلفة الرحلات المتقدم لمشغلي الأساطيل في المملكة العربية السعودية",
          "provider": {
            "@type": "Organization",
            "name": "لوجستيات"
          },
          "serviceType": "برنامج إدارة الأسطول",
          "areaServed": "المملكة العربية السعودية"
        }}
      />
      <main className="min-h-screen">
      {/* Header */}
      <header className="bg-white shadow-sm">
        <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            <div className="flex items-center">
              <Link to="/ar" className="text-2xl font-bold text-gray-900">Logistiat</Link>
            </div>
            <div className="hidden md:flex space-x-8 space-x-reverse">
              <Link to="/ar/features" className="text-gray-600 hover:text-gray-900">المميزات</Link>
              <Link to="/ar/pricing" className="text-gray-600 hover:text-gray-900">الأسعار</Link>
              <Link to="/ar/trip-costing" className="text-blue-600 font-medium">تكلفة الرحلات</Link>
              <Link to="/ar/contact" className="text-gray-600 hover:text-gray-900">اتصل بنا</Link>
              <Link to="/en" className="text-gray-600 hover:text-gray-900">English</Link>
            </div>
            <div className="flex space-x-4 space-x-reverse">
              <Link
                to="/login"
                className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 font-medium"
              >
                تسجيل الدخول
              </Link>
            </div>
          </div>
        </nav>
      </header>

      {/* Hero Section */}
      <section className="bg-gradient-to-r from-green-600 to-blue-600 text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-6xl font-bold mb-6">
            تكلفة الرحلات والربحية
          </h1>
          <p className="text-xl md:text-2xl mb-8 max-w-3xl mx-auto">
            احسب تكاليف الرحلات بدقة، تتبع مصاريف الوقود، وزيادة الربحية لكل شحنة في المملكة العربية السعودية.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/login"
              className="bg-white text-blue-600 px-8 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-colors"
            >
              ابدأ حساب التكاليف
            </Link>
            <Link
              to="/ar/contact"
              className="border-2 border-white text-white px-8 py-3 rounded-lg font-semibold hover:bg-white hover:text-blue-600 transition-colors"
            >
              طلب عرض توضيحي
            </Link>
          </div>
        </div>
      </section>

      {/* Trip Costing Features */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              إدارة شاملة لتكاليف الرحلات
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              تتبع كل المصاريف، احسب التكاليف بدقة، وحسّن ربحية أسطولك
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="bg-white p-8 rounded-xl shadow-lg">
              <div className="text-4xl mb-4">⛽</div>
              <h3 className="text-xl font-semibold mb-4">تتبع تكاليف الوقود</h3>
              <p className="text-gray-600">
                حساب تكاليف الوقود تلقائياً مع تكامل بترو آب. تتبع استهلاك الوقود والتكاليف لكل كيلومتر ومقاييس كفاءة الوقود.
              </p>
            </div>

            <div className="bg-white p-8 rounded-xl shadow-lg">
              <div className="text-4xl mb-4">💰</div>
              <h3 className="text-xl font-semibold mb-4">تسويات السائقين</h3>
              <p className="text-gray-600">
                احسب مدفوعات السائقين والعمولات والتسويات تلقائياً. تتبع مصاريف السائقين ومقاييس الأداء.
              </p>
            </div>

            <div className="bg-white p-8 rounded-xl shadow-lg">
              <div className="text-4xl mb-4">🔧</div>
              <h3 className="text-xl font-semibold mb-4">تكاليف الصيانة</h3>
              <p className="text-gray-600">
                تتبع مصاريف صيانة المركبات، جدولة الصيانة الوقائية، وحساب تكلفة الصيانة لكل كيلومتر.
              </p>
            </div>

            <div className="bg-white p-8 rounded-xl shadow-lg">
              <div className="text-4xl mb-4">📊</div>
              <h3 className="text-xl font-semibold mb-4">تحليل الربحية</h3>
              <p className="text-gray-600">
                تحليل الربحية في الوقت الفعلي لكل رحلة. قارن التكاليف الفعلية مع المقدرة وحدد فرص التحسين.
              </p>
            </div>

            <div className="bg-white p-8 rounded-xl shadow-lg">
              <div className="text-4xl mb-4">🧾</div>
              <h3 className="text-xl font-semibold mb-4">إدارة ضريبة القيمة المضافة</h3>
              <p className="text-gray-600">
                حسابات ضريبة القيمة المضافة التلقائية المتوافقة مع أنظمة الضرائب السعودية. إنتاج التقارير الضريبية والحفاظ على الامتثال.
              </p>
            </div>

            <div className="bg-white p-8 rounded-xl shadow-lg">
              <div className="text-4xl mb-4">📈</div>
              <h3 className="text-xl font-semibold mb-4">تحليلات التكاليف</h3>
              <p className="text-gray-600">
                تحليلات وتقارير متقدمة حول تكاليف الرحلات وكفاءة الوقود وأداء الأسطول. اتخذ قرارات مبنية على البيانات.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Cost Calculation Process */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              كيف تعمل تكلفة الرحلات
            </h2>
            <p className="text-xl text-gray-600">
              عملية بسيطة من 4 خطوات لحساب تكاليف الرحلات بدقة
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="bg-blue-600 text-white w-16 h-16 rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-4">1</div>
              <h3 className="text-xl font-semibold mb-2">إنشاء الشحنة</h3>
              <p className="text-gray-600">أدخل تفاصيل الشحنة والطريق ومعلومات المركبة</p>
            </div>

            <div className="text-center">
              <div className="bg-blue-600 text-white w-16 h-16 rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-4">2</div>
              <h3 className="text-xl font-semibold mb-2">تتبع المصاريف</h3>
              <p className="text-gray-600">تتبع الوقود تلقائياً وإدخال المصاريف يدوياً</p>
            </div>

            <div className="text-center">
              <div className="bg-blue-600 text-white w-16 h-16 rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-4">3</div>
              <h3 className="text-xl font-semibold mb-2">حساب التكاليف</h3>
              <p className="text-gray-600">حساب التكاليف في الوقت الفعلي مع ضريبة القيمة المضافة والضرائب</p>
            </div>

            <div className="text-center">
              <div className="bg-blue-600 text-white w-16 h-16 rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-4">4</div>
              <h3 className="text-xl font-semibold mb-2">إنتاج التقارير</h3>
              <p className="text-gray-600">تقارير تكاليف مفصلة وتحليل الربحية</p>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                زيادة ربحية أسطولك إلى الحد الأقصى
              </h2>
              <div className="space-y-6">
                <div className="flex items-start">
                  <div className="bg-green-100 p-2 rounded-lg ml-4">
                    <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold mb-2">تقليل تكاليف التشغيل</h3>
                    <p className="text-gray-600">حدد فرص توفير التكاليف وحسّن استهلاك الوقود</p>
                  </div>
                </div>

                <div className="flex items-start">
                  <div className="bg-green-100 p-2 rounded-lg ml-4">
                    <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold mb-2">تسعير دقيق</h3>
                    <p className="text-gray-600">ضع أسعار تنافسية بناءً على بيانات التكلفة الحقيقية</p>
                  </div>
                </div>

                <div className="flex items-start">
                  <div className="bg-green-100 p-2 rounded-lg ml-4">
                    <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold mb-2">تحسين التدفق النقدي</h3>
                    <p className="text-gray-600">تخطيط مالي أفضل مع توقعات التكاليف الدقيقة</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-br from-blue-50 to-green-50 p-8 rounded-2xl">
              <h3 className="text-2xl font-bold text-gray-900 mb-6">مثال على توفير التكاليف</h3>
              <div className="space-y-4">
                <div className="flex justify-between">
                  <span className="text-gray-600">تكاليف الوقود الشهرية:</span>
                  <span className="font-semibold">50,000 ريال</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">توفير التحسين (10%):</span>
                  <span className="font-semibold text-green-600">-5,000 ريال</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">تحسين الصيانة:</span>
                  <span className="font-semibold text-green-600">-3,000 ريال</span>
                </div>
                <hr className="border-gray-300" />
                <div className="flex justify-between text-lg">
                  <span className="font-semibold">التوفير الشهري:</span>
                  <span className="font-bold text-green-600">8,000 ريال</span>
                </div>
                <div className="flex justify-between text-xl">
                  <span className="font-bold">التوفير السنوي:</span>
                  <span className="font-bold text-green-600">96,000 ريال</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-blue-600 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            ابدأ تحسين تكاليف رحلاتك اليوم
          </h2>
          <p className="text-xl mb-8">
            انضم إلى مئات شركات النقل السعودية التي تستخدم لوجستيات لحساب تكاليف الرحلات بدقة وتحسين الربحية.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/login"
              className="bg-white text-blue-600 px-8 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-colors"
            >
              ابدأ التجربة المجانية
            </Link>
            <Link
              to="/ar/contact"
              className="border-2 border-white text-white px-8 py-3 rounded-lg font-semibold hover:bg-white hover:text-blue-600 transition-colors"
            >
              جدولة عرض توضيحي
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-4 gap-8">
            <div>
              <h3 className="text-xl font-bold mb-4">Logistiat</h3>
              <p className="text-gray-400">
                حل إدارة الأسطول الشامل لشركات النقل في المملكة العربية السعودية.
              </p>
            </div>
            <div>
              <h4 className="font-semibold mb-4">المميزات</h4>
              <ul className="space-y-2 text-gray-400">
                <li><Link to="/ar/features" className="hover:text-white">إدارة الأسطول</Link></li>
                <li><Link to="/ar/trip-costing" className="hover:text-white">تكلفة الرحلات</Link></li>
                <li><Link to="/ar/features" className="hover:text-white">إدارة السائقين</Link></li>
                <li><Link to="/ar/features" className="hover:text-white">تتبع الوقود</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4">الشركة</h4>
              <ul className="space-y-2 text-gray-400">
                <li><Link to="/ar/contact" className="hover:text-white">اتصل بنا</Link></li>
                <li><Link to="/ar/pricing" className="hover:text-white">الأسعار</Link></li>
                <li><Link to="/en" className="hover:text-white">English</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4">الدعم</h4>
              <ul className="space-y-2 text-gray-400">
                <li><Link to="/ar/contact" className="hover:text-white">مركز المساعدة</Link></li>
                <li><Link to="/ar/contact" className="hover:text-white">اتصل بالدعم</Link></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-gray-800 mt-8 pt-8 text-center text-gray-400">
            <p>&copy; 2024 Logistiat. جميع الحقوق محفوظة.</p>
          </div>
        </div>
      </footer>
      </main>
    </>
  );
};

export default TripCostingPageArabic;